import { Children, isValidElement, type ReactElement, type ReactNode } from "react";

// Tabella delle offerte sul telefono (Alfonso 2026-09-29, modello A): con la chat stretta una scheda
// per offerta invece della tabella larga, che usciva dal fumetto (597px su 278) e lasciava
// prezzo, quota e durata raggiungibili solo scorrendo di lato. Conta la larghezza della CHAT
// (container query `@3xl` = 48rem sull'elenco messaggi), non dello schermo: la pagina del QR
// resta una colonna stretta anche al computer. Con la chat larga resta la tabella.
// La tabella porta `aef-offerte`: va a capo dentro il fumetto invece di uscirne (index.css). Con la chat
// poco oltre i 48rem, per esempio accanto al riquadro della spesa, usciva di lato e l'ultima colonna
// restava tagliata (segnalazione Alfonso 19/10).
// Le altre tabelle della chat passano come sono.

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
}

function testo(n: HastNode | undefined): string {
  if (!n) return "";
  if (n.type === "text") return n.value ?? "";
  return (n.children ?? []).map(testo).join("");
}

function trova(n: HastNode | undefined, tag: string): HastNode[] {
  if (!n) return [];
  const qui = n.tagName === tag ? [n] : [];
  return qui.concat(...(n.children ?? []).map((c) => trova(c, tag)));
}

type ElementoConFigli = ReactElement<{ children?: ReactNode }>;

function figli(el: ReactNode, tipo: string): ElementoConFigli[] {
  return Children.toArray(el).filter(
    (c): c is ElementoConFigli => isValidElement(c) && c.type === tipo,
  );
}

export function TabellaChat({ node, children }: { node?: HastNode; children?: ReactNode }) {
  const intestazioni = trova(node, "th").map((th) => testo(th).trim());
  const col = (nome: string) => intestazioni.indexOf(nome);
  const iOfferta = col("Offerta");
  const iPrezzo = col("Prezzo energia");
  if (iOfferta < 0 || iPrezzo < 0) return <table>{children}</table>;

  const iNum = col("#");
  const iFornitore = col("Fornitore");
  const iQuota = col("Quota fissa");
  const iDurata = col("Durata · Fatturazione");
  const righe = figli(children, "tbody").flatMap((tb) => figli(tb.props.children, "tr"));

  return (
    <>
      <table className="aef-offerte hidden @3xl:table">{children}</table>
      <div className="@3xl:hidden not-prose space-y-2 mb-4" data-testid="offerte-schede">
        {righe.map((tr, r) => {
          const celle = figli(tr.props.children, "td").map((td) => td.props.children);
          const cella = (i: number) => (i >= 0 ? celle[i] : null);
          return (
            <div key={r} className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 text-slate-800">
              <div className="text-[15px] font-medium leading-snug">
                {iNum >= 0 && <span className="text-slate-500 mr-1.5">{cella(iNum)}</span>}
                {cella(iOfferta)}
              </div>
              {iFornitore >= 0 && <p className="text-sm text-slate-600 mt-0.5">{cella(iFornitore)}</p>}
              <div className="mt-1.5 text-lg tabular-nums [&_.aef-price-sub]:text-xs">{cella(iPrezzo)}</div>
              {(iQuota >= 0 || iDurata >= 0) && (
                <p className="text-sm text-slate-600 tabular-nums mt-0.5">
                  {iQuota >= 0 && <>Quota fissa {cella(iQuota)}</>}
                  {iQuota >= 0 && iDurata >= 0 && " · "}
                  {cella(iDurata)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
